import React, { useState, useEffect } from 'react';
import { 
  FiUsers, 
  FiPlus, 
  FiEdit2, 
  FiTrash2, 
  FiSave, 
  FiX, 
  FiShield,
  FiSettings,
  FiEye,
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiBell
} from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import RoleTemplateManager from './RoleTemplateManager';
import apiClient, { endpoints } from '../api/client';

const EnhancedRoleManagement = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  
  // State management
  const [activeTab, setActiveTab] = useState('subadmins');
  const [subAdmins, setSubAdmins] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedSubAdmin, setSelectedSubAdmin] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role_id: '',
    permissions: {
      users: { create: false, read: false, update: false, delete: false },
      certificates: { create: false, read: false, update: false, delete: false },
      tags: { create: false, read: false, update: false, delete: false },
      reminders: { create: false, read: false, update: false, delete: false },
      templates: { create: false, read: false, update: false, delete: false },
      reports: { create: false, read: false, update: false, delete: false },
      settings: { create: false, read: false, update: false, delete: false },
      audit_logs: { create: false, read: false, update: false, delete: false }
    }
  });

  const permissionCategories = [
    { key: 'users', label: 'Client Management', description: 'Manage client accounts and profiles' },
    { key: 'certificates', label: 'Certificate Management', description: 'Handle certificates and validations' },
    { key: 'tags', label: 'Tag Management', description: 'Add and manage tags' },
    { key: 'reminders', label: 'Reminder System', description: 'Set up and manage reminders' },
    { key: 'templates', label: 'Template Management', description: 'Add and edit templates' },
    { key: 'reports', label: 'Reports & Analytics', description: 'Generate and view reports' },
    { key: 'settings', label: 'System Settings', description: 'Configure system settings' },
    { key: 'audit_logs', label: 'Audit Logs', description: 'View system audit logs' }
  ];

  const permissions = ['create', 'read', 'update', 'delete'];

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [subAdminsResponse, rolesResponse] = await Promise.all([
        apiClient.get(endpoints.subAdmin.list),
        apiClient.get(endpoints.subAdmin.roles)
      ]);
      
      setSubAdmins(subAdminsResponse.data.data || []);
      setRoles(rolesResponse.data.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      showToast('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubAdmin = async (e) => {
    e.preventDefault();
    
    try {
      const response = await apiClient.post(endpoints.subAdmin.create, formData);
      if (response.data.success) {
        showToast('Sub Admin created successfully', 'success');
        fetchData();
        resetForm();
        setShowCreateModal(false);
      }
    } catch (error) {
      console.error('Error creating sub admin:', error);
      showToast(error.response?.data?.message || 'Failed to create Sub Admin', 'error');
    }
  };

  const handleUpdateSubAdmin = async (e) => {
    e.preventDefault();
    
    try {
      const response = await apiClient.put(endpoints.subAdmin.byId(selectedSubAdmin.id), formData);
      if (response.data.success) {
        showToast('Sub Admin updated successfully', 'success');
        fetchData();
        resetForm();
        setShowEditModal(false);
        setSelectedSubAdmin(null);
      }
    } catch (error) {
      console.error('Error updating sub admin:', error);
      showToast(error.response?.data?.message || 'Failed to update Sub Admin', 'error');
    }
  };

  const handleDeleteSubAdmin = async (subAdminId) => {
    if (!window.confirm('Are you sure you want to delete this Sub Admin? This action cannot be undone.')) {
      return;
    }
    
    try {
      const response = await apiClient.delete(endpoints.subAdmin.byId(subAdminId));
      if (response.data.success) {
        showToast('Sub Admin deleted successfully', 'success');
        fetchData();
      }
    } catch (error) {
      console.error('Error deleting sub admin:', error);
      showToast(error.response?.data?.message || 'Failed to delete Sub Admin', 'error');
    }
  };

  const handlePermissionChange = (category, permission, value) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [category]: {
          ...prev.permissions[category],
          [permission]: value
        }
      }
    }));
  };

  const handleRoleChange = (roleId) => {
    const selectedRole = roles.find(role => role.id === parseInt(roleId));
    if (selectedRole) {
      setFormData(prev => ({
        ...prev,
        role_id: roleId,
        permissions: { ...selectedRole.permissions }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        role_id: roleId
      }));
    }
  };

  const startEdit = (subAdmin) => {
    setSelectedSubAdmin(subAdmin);
    setFormData({
      name: subAdmin.name,
      email: subAdmin.email,
      role_id: subAdmin.role_id?.toString() || '',
      permissions: JSON.parse(subAdmin.role.permissions)
    });
    setShowEditModal(true);
  };

  const startView = (subAdmin) => {
    setSelectedSubAdmin({
      ...subAdmin,
      role: {
        ...subAdmin.role,
        permissions: JSON.parse(subAdmin.role.permissions)
      }
    });
    setShowViewModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      role_id: '',
      permissions: {
        users: { create: false, read: false, update: false, delete: false },
        certificates: { create: false, read: false, update: false, delete: false },
        tags: { create: false, read: false, update: false, delete: false },
        reminders: { create: false, read: false, update: false, delete: false },
        templates: { create: false, read: false, update: false, delete: false },
        reports: { create: false, read: false, update: false, delete: false },
        settings: { create: false, read: false, update: false, delete: false },
        audit_logs: { create: false, read: false, update: false, delete: false }
      }
    });
  };

  // Filter sub admins based on search and role filter
  const filteredSubAdmins = subAdmins.filter(subAdmin => {
    const matchesSearch = subAdmin.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = !filterRole || subAdmin.role_id?.toString() === filterRole;
    return matchesSearch && matchesRole;
  });

  const getPermissionCount = (permissions) => {
    if (!permissions) return 0;
    return Object.values(permissions).reduce((count, perms) => {
      return count + Object.values(perms).filter(p => p).length;
    }, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Role Management</h2>
          <p className="text-gray-600 pb-5">Manage Sub Admins and Role Assignments</p>
        </div>
        <button
          onClick={fetchData}
          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
        >
          <FiRefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 space-y-6">
        <div className="flex">
          <FiBell className="h-5 w-5 text-blue-400 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Role Management
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                Assign roles and permissions to team members. Define who can view, edit, or manage system components.
              </p>
              <p>
                i. First, create the Role Assignment
              </p>
              <p>
                ii. Then, create the Sub-Admin
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 pt-5">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('subadmins')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-all duration-200 ${
              activeTab === 'subadmins'
              ? 'border-brand-primary text-brand-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FiUsers className="inline h-4 w-4 mr-2" />
            Sub Admins ({subAdmins.length})
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-all duration-200 ${
              activeTab === 'templates'
              ? 'border-brand-primary text-brand-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FiShield className="inline h-4 w-4 mr-2" />
            Role Assignments ({roles.length})
          </button>
        </nav>
      </div>

      {/* Sub Admins Tab */}
      {activeTab === 'subadmins' && (
        <div className="space-y-6">
          {/* Controls */}
          <div className="flex flex-col sm:flex-row gap-4 justify-between pt-5">
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              <div className="relative flex-1 max-w-md">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search sub admins..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm transition-all duration-200"
                />
              </div>
              <div className="relative">
                <FiFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm transition-all duration-200"
                >
                  <option value="">All Roles</option>
                  {roles.map(role => (
                    <option key={role.id} value={role.id}>{role.display_name}</option>
                  ))}
                </select>
              </div>
            </div>
            <button
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-brand-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
            >
              <FiPlus className="h-4 w-4 mr-2" />
              Add Sub Admin
            </button>
          </div>

          {/* Sub Admins Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSubAdmins.map((subAdmin) => (
              <div key={subAdmin.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all duration-200">
                <div className="flex items-start justify-between">
                  <div className="flex items-center">
                    <div className="bg-blue-100 rounded-full p-2">
                      <FiUsers className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-gray-900">{subAdmin.name}</h3>
                      <p className="text-sm text-gray-500">{subAdmin.email}</p>
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => startView(subAdmin)}
                      className="text-gray-400 hover:text-blue-600 p-1"
                      title="View Details"
                    >
                      <FiEye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => startEdit(subAdmin)}
                      className="text-gray-400 hover:text-blue-600 p-1"
                      title="Edit"
                    >
                      <FiEdit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteSubAdmin(subAdmin.id)}
                      className="text-gray-400 hover:text-red-600 p-1"
                      title="Delete"
                    >
                      <FiTrash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                
                <div className="mt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Role:</span>
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {roles.find(r => r.id === subAdmin.role_id)?.display_name || 'Custom'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-500">Permissions:</span>
                    <span className="text-xs text-gray-700">
                      {getPermissionCount(subAdmin.role.permissions)} granted
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-500">Status:</span>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      subAdmin.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {subAdmin.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredSubAdmins.length === 0 && (
            <div className="text-center py-12">
              <FiUsers className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No Sub Admins Found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || filterRole ? 'Try adjusting your search or filter criteria.' : 'Get started by creating your first Sub Admin.'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Role Assignments Tab */}
      {activeTab === 'templates' && (
        <RoleTemplateManager />
      )}

      {/* Create Sub Admin Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-lg bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Add New Sub Admin</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleCreateSubAdmin} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative flex-1 max-w-md">
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 pl-5 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1 pl-5 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm transition-all duration-200"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Role Assignment</label>
                <select
                  value={formData.role_id}
                  onChange={(e) => handleRoleChange(e.target.value)}
                  className="mt-1 pl-5 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm transition-all duration-200"
                >
                  <option value="">Select a role or set custom permissions</option>
                  {roles.map(role => (
                    <option key={role.id} value={role.id}>{role.display_name}</option>
                  ))}
                </select>
              </div>

              {/* Permissions */}
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Permissions</label>
                <div className="space-y-4">
                  {permissionCategories.map(category => (
                    <div key={category.key} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">{category.label}</h4>
                          <p className="text-xs text-gray-500">{category.description}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {permissions.map(permission => (
                          <label key={permission} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData.permissions[category.key]?.[permission] || false}
                              onChange={(e) => handlePermissionChange(category.key, permission, e.target.checked)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-all duration-200"
                            />
                            <span className="ml-2 text-sm text-gray-700 capitalize">{permission}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div> */}

              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-brand-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                >
                  <FiSave className="h-4 w-4 mr-2" />
                  Add Sub Admin
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Sub Admin Modal */}
      {showEditModal && selectedSubAdmin && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-lg bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Edit Sub Admin</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleUpdateSubAdmin} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 pl-5 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1 pl-5 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm transition-all duration-200"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Role Assignment</label>
                <select
                  value={formData.role_id}
                  onChange={(e) => handleRoleChange(e.target.value)}
                  className="mt-1 pl-5 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm transition-all duration-200"
                >
                  <option value="">Select a role or set custom permissions</option>
                  {roles.map(role => (
                    <option key={role.id} value={role.id}>{role.display_name}</option>
                  ))}
                </select>
              </div>

              {/* Permissions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Permissions</label>
                <div className="space-y-4">
                  {permissionCategories.map(category => (
                    <div key={category.key} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">{category.label}</h4>
                          <p className="text-xs text-gray-500">{category.description}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {permissions.map(permission => (
                          <label key={permission} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData.permissions[category.key]?.[permission] || false}
                              onChange={(e) => handlePermissionChange(category.key, permission, e.target.checked)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-all duration-200"
                            />
                            <span className="ml-2 text-sm text-gray-700 capitalize">{permission}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-brand-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                >
                  <FiSave className="h-4 w-4 mr-2" />
                  Update Sub Admin
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Sub Admin Modal */}
      {showViewModal && selectedSubAdmin && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-3xl shadow-lg rounded-lg bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Sub Admin Details</h3>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedSubAdmin.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedSubAdmin.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {roles.find(r => r.id === selectedSubAdmin.role_id)?.display_name || 'Custom Role'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <span className={`mt-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    selectedSubAdmin.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {selectedSubAdmin.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Permissions</label>
                <div className="space-y-3">
                  {permissionCategories.map(category => {
                    const categoryPerms = selectedSubAdmin.role?.permissions?.[category.key] || {};
                    const hasAnyPermission = Object.values(categoryPerms).some(p => p);
                    
                    if (!hasAnyPermission) return null;
                    
                    return (
                      <div key={category.key} className="border border-gray-200 rounded-lg p-3">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">{category.label}</h4>
                        <div className="flex flex-wrap gap-2">
                          {permissions.map(permission => {
                            if (!categoryPerms[permission]) return null;
                            return (
                              <span
                                key={permission}
                                className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800"
                              >
                                {permission}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    startEdit(selectedSubAdmin);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                >
                  <FiEdit2 className="h-4 w-4 mr-2" />
                  Edit
                </button>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedRoleManagement;